import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, NavLink, Outlet, useNavigate, useParams, useResolvedPath } from 'react-router-dom';
import classNames from 'classnames';

import { PermissionKeys, useGetProductsByIdQuery, useGetAgreementsQuery } from '@client/api';
import {
  PageBreadcrumbs,
  PageNavigation,
  Skeleton,
  Typography,
  useRouteDocumentTitle,
} from '@client/toolkit';

import { PageListLayout } from '../../../components/layout/page-list-layout';

import { ProductDeleteModal } from './components';

import { BackButton, PageContentAnimation } from '../../../components';
import { ProductsPaths } from '../products.router';
import { PermissionBasedActionButton } from '../../../components/permission-based-button/permission-based-button';

export const ProductRoute = () => {
  const [deleting, setDeleting] = useState<boolean>(false);

  const { t, ready } = useTranslation('products');
  const { t: tCommon } = useTranslation('common');

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const productPath = useResolvedPath(ProductsPaths.Product);

  const {
    data: product,
    isFetching,
    isError,
    isSuccess,
    isLoading,
  } = useGetProductsByIdQuery({ id: id ?? '' }, { skip: id == null });

  const { data: agreementsData } = useGetAgreementsQuery(
    { 'productId.Equal': id, 'pagination.Limit': 0, 'pagination.Offset': 0 },
    { skip: id == null },
  );
  const agreementsCount = agreementsData?.totalCount ?? 0;

  const handleBack = () => {
    navigate(generatePath(ProductsPaths.Products));
  };

  useRouteDocumentTitle(t('product.documentTitle'), productPath.pathname);

  const handleClickDeleteProduct = () => {
    setDeleting(true);
  };

  const handleCloseDeleteProduct = () => {
    setDeleting(false);
  };

  const handleConfirmClickDeleteProduct = () => {
    navigate(generatePath(ProductsPaths.Products));
    setDeleting(false);
  };

  useEffect(() => {
    if (isLoading) return;
    if (isError) navigate(generatePath(ProductsPaths.Products), { replace: true });
    if (!isSuccess) return;
  }, [isError, isLoading, isSuccess, navigate]);

  const tabs = useMemo(() => {
    if (!id) return [];
    return [
      {
        key: 'details',
        label: t('detailTabs.details'),
        to: generatePath(ProductsPaths.Product, { id }),
        end: true,
      },
      {
        key: 'agreements',
        label: `${t('detailTabs.agreements')} (${agreementsCount})`,
        to: generatePath(ProductsPaths.ProductAgreements, { id }),
        end: false,
      },
    ];
  }, [t, id, agreementsCount]);

  return (
    <PageListLayout>
      <PageNavigation>
        <PageBreadcrumbs className="py-4">
          <PageBreadcrumbs.Link onClick={handleBack}>
            {ready ? t('overview') : <Skeleton variant="text" width="w-24" />}
          </PageBreadcrumbs.Link>
          <PageBreadcrumbs.Link>
            {ready ? t('product.title') : <Skeleton variant="text" width="w-24" />}
          </PageBreadcrumbs.Link>
          <PageBreadcrumbs.Link>
            {isFetching ? <Skeleton variant="text" width="w-32" /> : product?.name}
          </PageBreadcrumbs.Link>
        </PageBreadcrumbs>
      </PageNavigation>
      <PageContentAnimation>
        <div className="flex w-full justify-between mb-4">
          <BackButton />
          <div className="flex gap-2">
            <PermissionBasedActionButton
              onClick={handleClickDeleteProduct}
              permissions={[PermissionKeys.Tenant.ProductDelete]}
              title={tCommon('delete')}
              disabled={isFetching || id == null}
              loading={isFetching}
              color="danger"
            />
          </div>
        </div>
      </PageContentAnimation>

      <PageContentAnimation className="mb-4">
        <div className="flex gap-8 border-b border-border w-full">
          {tabs.map((tab) => (
            <NavLink
              key={tab.key}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) =>
                classNames(
                  'pb-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'text-brand-primary border-b-2 border-brand-primary'
                    : 'text-white hover:text-brand-primary',
                )
              }
            >
              {tab.label}
            </NavLink>
          ))}
        </div>
      </PageContentAnimation>

      <PageContentAnimation className="space-y-4 px-4 pt-2 pb-8 rounded-lg shadow-lg border border-border">
        <div className="mt-3 mb-1">
          {isFetching ? (
            <Skeleton variant="text" className="w-[40%]" />
          ) : (
            <Typography variant="section">
              {product?.name}
            </Typography>
          )}
        </div>
        <Outlet />
      </PageContentAnimation>

      {product != null && !isFetching && (
        <ProductDeleteModal
          product={product}
          open={deleting}
          onCancel={handleCloseDeleteProduct}
          onDelete={handleConfirmClickDeleteProduct}
        />
      )}
    </PageListLayout>
  );
};
